/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "../../components/ui/sonner";
import { Loader2 } from "lucide-react";
import { inp, lbl, money, today } from "../finance/assets/assetUtils";

/* 入职：从闲置设备中勾选分配给员工 */
export function TransitionAssignDialog({ zh, tid, employee, meta, onClose, onDone }) {
  const { api } = useApp();
  const T = (z, e) => (zh ? z : e);
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState(null);
  const [sel, setSel] = useState([]);
  const [note, setNote] = useState(T("入职配发", "Onboarding issue"));
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/hrms/transitions/${tid}/assets`, { params: { q } }).then(({ data }) => setAvail(data.available || [])).catch(() => setAvail([])); }, [api, tid, q]);
  const grouped = useMemo(() => {
    const g = {};
    (avail || []).forEach((a) => { (g[a.category] = g[a.category] || []).push(a); });
    return g;
  }, [avail]);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = async () => {
    if (!sel.length) return toast.error(T("请勾选至少一台设备", "Select at least one item"));
    setBusy(true);
    try {
      const { data } = await api.post(`/hrms/transitions/${tid}/assets/assign`, { assetIds: sel, note, date });
      toast.success(T(`已分配 ${data.assigned.length} 台设备${data.taskTicked ? "，HR「装备发放」任务已自动勾选" : ""}`, `Assigned ${data.assigned.length} item(s)`));
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || T("分配失败", "Failed")); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="tr-assets-assign-dialog">
      <DialogHeader><DialogTitle>{T("分配设备给", "Assign equipment to")} {employee?.name}</DialogTitle><DialogDescription>{T("仅显示「闲置」状态的公司资产；本分公司设备优先排列。分配后自动生成《资产领用单》。", "Idle assets only; same-branch first. An assignment form PDF is generated.")}</DialogDescription></DialogHeader>
      <input className={inp} data-testid="tr-assets-q" placeholder={T("搜索名称 / 编号 / 序列号 / 品牌…", "Search…")} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-[42vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="tr-assets-available">
        {avail === null ? <div className="py-8 text-center text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          : avail.length === 0 ? <p className="py-8 text-center text-sm text-slate-300" data-testid="tr-assets-available-empty">{T("没有闲置设备。请先在财务中心 → 固定资产 新增或导入。", "No idle equipment. Add or import in Finance Center → Fixed Assets.")}</p>
          : Object.keys(grouped).map((cat) => (
            <div key={cat}>
              <p className="text-[11px] font-bold text-slate-500 bg-slate-50 px-3 py-1">{lbl(meta?.categories, cat, zh)}（{grouped[cat].length}）</p>
              {grouped[cat].map((a) => (
                <label key={a.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`tr-assets-pick-${a.id}`}>
                  <input type="checkbox" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} className="accent-emerald-600" />
                  <span className="flex-1 min-w-0"><span className="font-semibold text-slate-700">{a.name}</span><span className="block text-[10px] text-slate-400 font-mono">{a.id}{a.serialNo ? ` · SN ${a.serialNo}` : ""}{a.brand ? ` · ${a.brand}` : ""} · {lbl(meta?.branches, a.branch, zh)}{a.location ? ` · ${a.location}` : ""}</span></span>
                  <span className="text-xs text-slate-500">{money(a.cost)}</span>
                </label>
              ))}
            </div>
          ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm text-slate-600">{T("分配日期", "Date")}<input type="date" className={inp} data-testid="tr-assets-date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="text-sm text-slate-600">{T("说明", "Note")}<input className={inp} data-testid="tr-assets-note" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
      <div className="flex-wrap md:flex-nowrap flex items-center justify-between">
        <span className="text-xs text-slate-500" data-testid="tr-assets-selected">{T(`已勾选 ${sel.length} 台`, `${sel.length} selected`)}</span>
        <div className="flex-wrap md:flex-nowrap flex gap-2">
          <button onClick={onClose} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
          <button data-testid="tr-assets-assign-submit" disabled={busy || !sel.length} onClick={submit} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("确认分配", "Assign")}</button>
        </div>
      </div>
    </DialogContent></Dialog>
  );
}
